//
// Copyright (c) 2011-2020 by Curalate, Inc.
//
import { IRootScopeService } from 'angular';
import { IStateService } from 'angular-ui-router';
import { useEffect, useState } from 'react';

import getAngularInjector from '@react-bridge/getAngularInjector';

import { IStateData } from 'app/initialization/routing/stateHelpers';
import {
  StringifiedStateParams,
  TypedRoutableStateName,
} from 'app/initialization/routing/stateParamModels';
import logger from 'app/logger/logger';
import { RoutableStateName } from 'app/top-level-states';

/**
 * Returns the current state and state params from the AngularJS UI Router service.
 *
 * @param state The expected state. This param is used only to infer the types of the state params returned from this hook,
 * and isn't actually used in the function implementation at all.
 *
 *  @note Approximately matches ui-router official:
 *  https://ui-router.github.io/react/docs/latest/modules/react_hooks.html#usecurrentstateandparams
 */
export function useCurrentStateAndParams<T extends TypedRoutableStateName>(
  state: T,
): {
  state: T;
  params: StringifiedStateParams<T>;
};

/**
 * Returns the current state and state params from the AngularJS UI Router service.
 *
 *  @note Approximately matches ui-router official:
 *  https://ui-router.github.io/react/docs/latest/modules/react_hooks.html#usecurrentstateandparams
 */
export function useCurrentStateAndParams<T extends {} = {}>(): {
  state: RoutableStateName;
  params: T;
};

/**
 * Use the current state and params.
 *
 * @note Approximately matches ui-router official:
 *  https://ui-router.github.io/react/docs/latest/modules/react_hooks.html#usecurrentstateandparams
 */
export function useCurrentStateAndParams<T extends {} = {}>(): {
  state: RoutableStateName;
  params: T;
} {
  // TODO: useAngularInjector? Probably unnecessary...
  const $injector = getAngularInjector();
  if (!$injector) {
    logger.console.error(
      'useCurrentStateAndParams called outside of angular context!',
    );
    throw Error('Angular Injector unavailable');
  }
  const $state: IStateService = $injector.get('$state');
  const [currentState, setCurrentState] = useState<RoutableStateName>(
    $state.current.name as RoutableStateName,
  );
  const [currentParams, setCurrentParams] = useState($state.params as T);

  useEffect(() => {
    const $rootScope: IRootScopeService = $injector.get('$rootScope');
    return $rootScope.$on('$stateChangeSuccess', () => {
      setCurrentState($state.current.name as RoutableStateName);
      setCurrentParams($state.params as T);
    });
  }, []);

  return {
    state: currentState,
    params: currentParams,
  };
}

export function useCurrentStateData(): {
  stateData: IStateData;
} {
  const $injector = getAngularInjector();
  if (!$injector) {
    logger.console.error(
      'useCurrentStateData called outside of angular context!',
    );
    throw Error('Angular Injector unavailable');
  }
  const $state: IStateService = $injector.get('$state');
  const [currentStateData, setCurrentStateData] = useState<IStateData>(
    $state.current.data as IStateData,
  );

  useEffect(() => {
    const $rootScope: IRootScopeService = $injector.get('$rootScope');
    return $rootScope.$on('$stateChangeSuccess', () => {
      setCurrentStateData($state.current.data as IStateData);
    });
  }, []);

  return {
    stateData: currentStateData,
  };
}
